/**
 * Zentrale Design-Tokens fuer Status-, Prioritaets- und Modul-Farben.
 * Verbraucher: StatusBadge, PriorityBadge, Card-Header, etc.
 *
 * Regel: NIEMALS lokale Farb-Maps in Pages – immer hier erweitern.
 */

export type BadgeVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'brand'
  | 'accent'

/**
 * Mapping von Status-Strings (aus Tickets, Invoices, Orders, Marketplace etc.)
 * auf eine Badge-Variante. Unbekannte Status fallen auf `neutral` zurueck.
 */
export const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  // Tickets
  new: 'info',
  open: 'info',
  in_progress: 'brand',
  waiting: 'warning',
  waiting_customer: 'warning',
  waiting_goods: 'warning',
  waiting_appointment: 'warning',
  resolved: 'success',
  closed: 'neutral',
  cancelled: 'danger',
  suspended: 'warning',

  // Invoices / Buchhaltung
  draft: 'neutral',
  pending: 'neutral',
  extracted: 'info',
  matched: 'info',
  approved: 'warning',
  booked: 'success',
  applied: 'info',
  rejected: 'danger',
  paid: 'success',
  unpaid: 'warning',
  overdue: 'danger',
  partially_paid: 'warning',
  written_off: 'neutral',

  // SAP Bank Statement Status
  bssdraft: 'info',
  bssexecuted: 'success',
  bssold: 'neutral',

  // Orders / SAP
  confirmed: 'success',
  shipped: 'info',
  delivered: 'success',
  returned: 'warning',

  // Shipping / Versand
  queued: 'info',
  processing: 'brand',
  sent: 'success',
  printed: 'warning',
  dispatched: 'info',

  // Marketplace / Generisch
  active: 'success',
  inactive: 'neutral',
  archived: 'neutral',
  planned: 'warning',
  error: 'danger',
  warning: 'warning',
  success: 'success',
  completed: 'success',
  failed: 'danger',

  // Lizenzen (ESET, ...)
  expired: 'danger',
  expiring_soon: 'warning',

  // Monitoring / RMM (CWA Alerts, Agents) / Netzwerk / Geraete
  online: 'success',
  offline: 'danger',
  critical: 'danger',
  information: 'info',

  // Callbacks / Telefonie
  done: 'success',

  // Feature-Requests
  submitted: 'info',
  reviewing: 'accent',
  implemented: 'success',

  // Neukundengewinnung / Prospects
  qualified: 'info',
  draft_letter: 'warning',
  letter_sent: 'warning',
  letter_failed: 'danger',
  follow_up: 'accent',
  interested: 'success',
  meeting_scheduled: 'success',
  converted: 'success',
  not_interested: 'danger',
  nurture: 'neutral',

  // Letter-Status (Kundenschreiber / Pensaki)
  sending: 'warning',

  // Kundenschreiber-Order-Status
  writing: 'brand',
  quality_check: 'warning',

  // Zeiterfassung / Team-Status
  working: 'success',
  paused: 'warning',

  // Artikel / SAP-Sync
  synced: 'success',
  local_only: 'neutral',
  conflict: 'warning',
  orphaned: 'danger',
  name_mismatch: 'warning',
  missing_in_sap: 'info',
  ok: 'success',
  proposed: 'info',

  // Liquiditaets-Status (Konzern-Forecast / Plan-Editor)
  // 'ok' ist weiter oben schon als success gemappt
  dispo: 'warning',
  kritisch: 'danger',
  'zahlungsunfähig': 'danger',
  zahlungsunfaehig: 'danger',

  // Kundenportal – Sicherheitsstatus (flache Keys wie von der API geliefert)
  partial: 'warning',
  missing: 'neutral',

  // Kundenportal – Sicherheitsstatus (namespaced Alias-Keys, falls benoetigt)
  security_active: 'success',
  security_partial: 'warning',
  security_missing: 'neutral',

  // Kundenportal – SAP Dokumenten-Status
  // SAP-BO gibt i.d.R. 'O' (Open), 'C' (Closed), 'P' (Printed), 'L' (Locked).
  document_open: 'info',
  document_closed: 'success',
  document_cancelled: 'danger',
  document_printed: 'neutral',
  document_locked: 'warning',
  bost_open: 'info',
  bost_close: 'success',
  bost_delivered: 'success',
  bost_cancel: 'danger',

  // Vertraege — Aliase mit contract_-Praefix (Status 'draft', 'active', 'paused',
  // 'expired' sind weiter oben in der Map bereits definiert).
  terminated: 'neutral',
  contract_draft: 'neutral',
  contract_active: 'success',
  contract_paused: 'warning',
  contract_terminated: 'neutral',
  contract_expired: 'danger',

  // Vertragsrichtung
  contract_outbound: 'brand',
  contract_inbound: 'info',

  // Reconciliation-Findings
  finding_open: 'warning',
  finding_acknowledged: 'info',
  finding_resolved: 'success',
  finding_ignored: 'neutral',
  finding_auto_resolved: 'success',

  // Billing-Runs
  // Preisanpassungen (Etappe 10)
  adjustment_preview: 'neutral',
  adjustment_applied: 'success',
  adjustment_reverted: 'warning',

  // SLA-Gutschriften (Etappe 11)
  sla_draft: 'neutral',
  sla_approved: 'info',
  sla_invoiced: 'success',
  sla_rejected: 'warning',

  billing_draft: 'neutral',
  billing_approved: 'info',
  billing_submitted: 'brand',
  billing_closed: 'success',
  billing_error: 'danger',
  // Auto-billing run statuses
  billing_auto_pending: 'neutral',
  billing_auto_dry_run: 'info',
  billing_auto_awaiting_approval: 'warning',
  billing_auto_approved: 'success',
  billing_auto_running: 'brand',
  billing_auto_completed: 'success',
  billing_auto_failed: 'danger',
  billing_auto_cancelled: 'neutral',
  // Billing-Proposals (CODA-1724/1736)
  pending_approval: 'warning',
  sent_to_sap: 'success',
  sap_failed: 'danger',
  // Billing-Item SAP-Status
  sap_pending: 'neutral',
  sap_submitted: 'info',
  sap_confirmed: 'success',
  sap_error: 'danger',

  // Vertragsdokumente
  document_draft: 'neutral',
  document_finalized: 'info',
  document_sent_for_signature: 'brand',
  document_signed: 'success',
  document_declined: 'danger',
  document_expired: 'warning',
  document_error: 'danger',

  // Angebotsmodul (KI-gestuetzt)
  quote_draft: 'neutral',
  quote_ready: 'info',
  quote_exported: 'success',
  quote_archived: 'neutral',
  ready: 'info',
  exported: 'success',

  // Module-Locks (Code-Sperre)
  locked: 'danger',
  unlocked: 'success',
  bugfix: 'warning',
  feature: 'brand',

  // Clearance / service approval (review run)
  running: 'brand',
  cleared: 'success',
  blocked: 'danger',

  // Leistungsfreigabe (Dispo)
  leistung_pending: 'neutral',
  leistung_approved: 'success',
  leistung_flagged: 'danger',

  // AI plausibility check result
  ai_bestanden: 'success',
  ai_beanstandet: 'danger',
}

/**
 * Menschenlesbare Labels (DE) fuer Status-Werte. Fallback: Status selbst.
 */
export const STATUS_LABELS: Record<string, string> = {
  new: 'Neu',
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  waiting: 'Wartend',
  waiting_customer: 'Wartet auf Kunde',
  waiting_goods: 'Wartet auf Ware',
  waiting_appointment: 'Wartet auf Termin',
  resolved: 'Gelöst',
  closed: 'Geschlossen',
  cancelled: 'Gekündigt',
  suspended: 'Gesperrt',
  draft: 'Entwurf',
  pending: 'Ausstehend',
  extracted: 'Erkannt',
  matched: 'Zugeordnet',
  approved: 'Freigegeben',
  booked: 'Gebucht',
  applied: 'Gebucht',
  rejected: 'Abgelehnt',
  paid: 'Bezahlt',
  unpaid: 'Offen',
  overdue: 'Überfällig',
  partially_paid: 'Teilbezahlt',
  written_off: 'Ausgebucht',
  confirmed: 'Bestätigt',
  shipped: 'Versendet',
  delivered: 'Geliefert',
  returned: 'Retourniert',
  queued: 'In Warteschlange',
  processing: 'In Bearbeitung',
  sent: 'Versendet',
  printed: 'Gedruckt',
  dispatched: 'Abgesendet',
  active: 'Aktiv',
  inactive: 'Inaktiv',
  archived: 'Archiviert',
  planned: 'Geplant',
  error: 'Fehler',
  bssdraft: 'Entwurf',
  bssexecuted: 'Fest gebucht',
  bssold: 'Archiviert',
  done: 'Erledigt',
  expired: 'Abgelaufen',
  expiring_soon: 'Läuft ab',
  online: 'Online',
  offline: 'Offline',
  critical: 'Kritisch',
  information: 'Information',
  completed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
  submitted: 'Eingereicht',
  reviewing: 'In Bewertung',
  implemented: 'Umgesetzt',

  // Neukundengewinnung / Prospects
  qualified: 'Qualifiziert',
  draft_letter: 'Brief-Entwurf offen',
  letter_sent: 'Brief versandt',
  letter_failed: 'Versandfehler',
  follow_up: 'Follow-up',
  interested: 'Interesse',
  meeting_scheduled: 'Termin',
  converted: 'Gewonnen',
  not_interested: 'Kein Interesse',
  nurture: 'Nurture',

  // Letter-Status
  sending: 'In Druck',

  // Kundenschreiber-Order-Status
  writing: 'Wird geschrieben',
  quality_check: 'Qualitätskontrolle',

  // Zeiterfassung / Team-Status
  working: 'Arbeitet',
  paused: 'Pause',

  // Artikel / SAP-Sync
  synced: 'Synchronisiert',
  local_only: 'Nur lokal',
  conflict: 'Konflikt',
  orphaned: 'Verwaist',
  name_mismatch: 'Name falsch',
  missing_in_sap: 'Fehlt in SAP',
  ok: 'OK',
  proposed: 'Vorgeschlagen',

  // Liquiditaets-Status
  dispo: 'Dispo',
  kritisch: 'Kritisch',
  'zahlungsunfähig': 'Zahlungsunfähig',
  zahlungsunfaehig: 'Zahlungsunfähig',

  // Kundenportal – Sicherheitsstatus (flach)
  partial: 'Teilweise',
  missing: 'Nicht abgedeckt',

  // Kundenportal – Sicherheitsstatus (namespaced Alias-Keys)
  security_active: 'Enthalten',
  security_partial: 'Teilweise',
  security_missing: 'Nicht abgedeckt',

  // Kundenportal – SAP Dokumenten-Status
  document_open: 'Offen',
  document_closed: 'Abgeschlossen',
  document_cancelled: 'Storniert',
  document_printed: 'Gedruckt',
  document_locked: 'Gesperrt',
  bost_open: 'Offen',
  bost_close: 'Abgeschlossen',
  bost_delivered: 'Geliefert',
  bost_cancel: 'Storniert',

  // Vertraege — Aliase mit contract_-Praefix (fuer die generischen Werte
  // greift der Fallback in `getStatusLabel` auf bereits oben gepflegte
  // Labels wie 'active'/'paused' zurueck).
  terminated: 'Gekündigt',
  contract_draft: 'Entwurf',
  contract_active: 'Aktiv',
  contract_paused: 'Pausiert',
  contract_terminated: 'Gekündigt',
  contract_expired: 'Abgelaufen',

  // Vertragsrichtung
  contract_outbound: 'Ausgehend',
  contract_inbound: 'Eingehend',

  // Reconciliation-Findings
  finding_open: 'Offen',
  finding_acknowledged: 'Zur Kenntnis',
  finding_resolved: 'Erledigt',
  finding_ignored: 'Ignoriert',
  finding_auto_resolved: 'Auto erledigt',

  // Billing-Runs
  adjustment_preview: 'Entwurf',
  adjustment_applied: 'Angewendet',
  adjustment_reverted: 'Zurückgenommen',

  // SLA-Gutschriften (Etappe 11)
  sla_draft: 'Entwurf',
  sla_approved: 'Freigegeben',
  sla_invoiced: 'Verrechnet',
  sla_rejected: 'Abgelehnt',

  billing_draft: 'Entwurf',
  billing_approved: 'Freigegeben',
  billing_submitted: 'An SAP übergeben',
  billing_closed: 'Abgeschlossen',
  billing_error: 'Fehler',
  // Auto-billing run statuses
  billing_auto_pending: 'Ausstehend',
  billing_auto_dry_run: 'Testlauf',
  billing_auto_awaiting_approval: 'Zur Freigabe',
  billing_auto_approved: 'Freigegeben',
  billing_auto_running: 'Läuft',
  billing_auto_completed: 'Abgeschlossen',
  billing_auto_failed: 'Fehlgeschlagen',
  billing_auto_cancelled: 'Abgebrochen',
  // Billing-Proposals (CODA-1724/1736)
  pending_approval: 'Freigabe ausstehend',
  sent_to_sap: 'An SAP übergeben',
  sap_failed: 'SAP-Fehler',
  // Billing-Item SAP-Status
  sap_pending: 'Wartend',
  sap_submitted: 'Übergeben',
  sap_confirmed: 'Bestätigt',
  sap_error: 'Fehler',

  // Vertragsdokumente
  document_draft: 'Entwurf',
  document_finalized: 'Final',
  document_sent_for_signature: 'Zur Unterschrift',
  document_signed: 'Signiert',
  document_declined: 'Abgelehnt',
  document_expired: 'Abgelaufen',
  document_error: 'Fehler',

  // Angebotsmodul (KI-gestuetzt)
  quote_draft: 'Entwurf (lokal)',
  quote_ready: 'Fertig',
  quote_exported: 'An SAP übergeben',
  quote_archived: 'Archiviert',
  ready: 'Fertig',
  exported: 'An SAP übergeben',

  // Module-Locks (Code-Sperre)
  locked: 'Gesperrt',
  unlocked: 'Aktiv',
  bugfix: 'Bug-Fix',
  feature: 'Feature',

  // Clearance / service approval (review run)
  running: 'Läuft',
  cleared: 'Freigegeben',
  blocked: 'Blockiert',

  // Leistungsfreigabe (Dispo)
  leistung_pending: 'Ausstehend',
  leistung_approved: 'Freigegeben',
  leistung_flagged: 'Beanstandet',

  // AI plausibility check result
  ai_bestanden: 'KI: Bestanden',
  ai_beanstandet: 'KI: Beanstandet',
}

export type Priority =
  | 'low'
  | 'normal'
  | 'medium'
  | 'high'
  | 'urgent'
  | 'critical'
  | 'nice_to_have'
  | 'important'

export const PRIORITY_VARIANTS: Record<Priority, BadgeVariant> = {
  low: 'success',
  normal: 'info',
  medium: 'warning',
  high: 'warning',
  urgent: 'danger',
  critical: 'danger',
  nice_to_have: 'neutral',
  important: 'warning',
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Niedrig',
  normal: 'Normal',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend',
  critical: 'Kritisch',
  nice_to_have: 'Nice to have',
  important: 'Wichtig',
}

/**
 * Tailwind-Klasse fuer einen linken Border-Indikator pro Prioritaet.
 * Wird genutzt wenn eine Prioritaet als linker Rand an einer Karte visualisiert
 * werden soll, statt als Badge-Pille.
 */
export const PRIORITY_BORDER_CLASSES: Record<Priority, string> = {
  low: 'border-l-gray-300',
  normal: 'border-l-blue-300',
  medium: 'border-l-amber-400',
  high: 'border-l-red-500',
  urgent: 'border-l-red-600',
  critical: 'border-l-red-700',
  nice_to_have: 'border-l-gray-300',
  important: 'border-l-amber-500',
}

/**
 * Hilfsfunktion: Status-String → Badge-Variante, mit Fallback.
 * Akzeptiert case-insensitive Werte und konvertiert Leerzeichen/Bindestriche zu Unterstrichen.
 */
export function resolveStatusVariant(status: string | number | null | undefined): BadgeVariant {
  if (status == null || status === '') return 'neutral'
  const key = String(status).toLowerCase().replace(/[\s-]+/g, '_')
  return STATUS_VARIANTS[key] ?? 'neutral'
}

export function resolveStatusLabel(status: string | number | null | undefined): string {
  if (status == null || status === '') return '—'
  const key = String(status).toLowerCase().replace(/[\s-]+/g, '_')
  return STATUS_LABELS[key] ?? String(status)
}

/**
 * Mapping von Kalender-Event-Quellen auf Badge-Varianten.
 * Verbraucher: KalenderPage (Event-Pillen, Legende).
 */
export type CalendarEventSource = 'm365' | 'tanss' | 'internal'

export const EVENT_SOURCE_VARIANTS: Record<CalendarEventSource, BadgeVariant> = {
  m365: 'info',
  tanss: 'brand',
  internal: 'accent',
}

export const EVENT_SOURCE_LABELS: Record<CalendarEventSource, string> = {
  m365: 'M365',
  tanss: 'TANSS',
  internal: 'Intern',
}
